import { For, createEffect, createSignal, onCleanup, onMount } from 'solid-js';
import type { Game, Round } from '../../game/Game';
import { formatMultiplier } from '../../utils/format';
import { badgeTier } from '../../utils/tiers';
import { MAX_HISTORY_COMPACT } from '../../game/constants';
import { gameApi, isRealMode } from '../../api';
import { gameStore } from '../../store/gameStore';

type Badge = { id: string; multiplier: number; tier: number };

const reducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export default function RoundHistoryTop() {
  const [items, setItems] = createSignal<Badge[]>([]);
  const [latest, setLatest] = createSignal<string | null>(null);
  let mounted = false;
  let game: Game | undefined;
  let wrapper: HTMLElement | null = null;
  let viewport: HTMLElement | null = null;
  let strip: HTMLElement | null = null;
  let ro: ResizeObserver | null = null;
  let timer: number | null = null;
  let userScroll = false;

  const show = () => {
    if (!wrapper || !items().length) return;
    wrapper.removeAttribute('hidden');
    wrapper.setAttribute('aria-hidden', 'false');
    if (!wrapper.parentElement) {
      const host = document.querySelector('.game-stage .game-main')?.parentElement || document.querySelector('.game-stage');
      host?.insertBefore(wrapper, host.firstChild || null);
    }
  };

  const syncOverflow = () => {
    if (!wrapper || !viewport) return;
    wrapper.setAttribute('data-overflow', viewport.scrollWidth > viewport.clientWidth + 1 ? 'true' : 'false');
  };

  const scrollEnd = (smooth: boolean) => {
    if (!viewport) return;
    viewport.scrollTo({ left: Math.max(0, viewport.scrollWidth - viewport.clientWidth), behavior: smooth ? 'smooth' : 'auto' });
    userScroll = false;
  };

  const push = (id: string, mult: number, animate?: boolean) => {
    if (animate) setLatest(id);
    setItems(prev => {
      const existingIndex = prev.findIndex(item => item.id === id);
      if (existingIndex >= 0) {
        const next = [...prev];
        next[existingIndex] = { ...next[existingIndex], multiplier: mult, tier: badgeTier(mult) };
        return next;
      }
      const next = [...prev, { id, multiplier: mult, tier: badgeTier(mult) }];
      return next.length > MAX_HISTORY_COMPACT ? next.slice(next.length - MAX_HISTORY_COMPACT) : next;
    });
    queueMicrotask(() => {
      syncOverflow();
      if (!userScroll) scrollEnd(!reducedMotion());
      if (animate) {
        if (timer) window.clearTimeout(timer);
        timer = window.setTimeout(() => setLatest(null), 420);
      }
    });
  };

  const applyStoreHistory = () => {
    const rounds = gameStore.roundHistory();
    if (!rounds.length) return;

    const next: Badge[] = [];
    const seen = new Set<string>();

    for (const round of rounds.slice(0, MAX_HISTORY_COMPACT).reverse()) {
      const id = String(round.id || '');
      if (!id) continue;

      const roundId = Number(id.replace('ws_round_', '').replace('rnd_', ''));
      if (!Number.isFinite(roundId)) continue;

      const mult = Number(round.crashPoint ?? round.multiplier ?? 0);
      if (!Number.isFinite(mult) || mult <= 0) continue;
      const normalizedId = `rnd_${roundId}`;
      if (seen.has(normalizedId)) continue;
      seen.add(normalizedId);
      next.push({ id: normalizedId, multiplier: mult, tier: badgeTier(mult) });
    }

    if (!next.length) return;
    setItems(next);
    queueMicrotask(() => { syncOverflow(); scrollEnd(false); });
    show();
  };

  const seed = () => {
    if (!game) return;
    try {
      const hist = game.getRoundHistory?.() as Round[] | undefined;
      if (hist?.length) {
        setItems(hist.slice(0, MAX_HISTORY_COMPACT).reverse().map(r => ({ id: r.id, multiplier: r.multiplier, tier: badgeTier(r.multiplier) })));
        queueMicrotask(() => { syncOverflow(); scrollEnd(false); });
      }
    } catch {}
    show();
  };

  const attach = () => {
    if (mounted) return;
    game = (window as any).memePumpApp?.getGame?.();
    if (!game) return;
    mounted = true;

    wrapper = document.querySelector('.round-history-top.compact');
    viewport = document.querySelector('.round-history-top .round-history-viewport');
    strip = document.querySelector('#round-history-top-strip');
    if (wrapper) wrapper.setAttribute('data-overflow', 'false');

    if (viewport) {
      const onScroll = () => {
        userScroll = !(viewport!.scrollLeft + viewport!.clientWidth >= viewport!.scrollWidth - 2);
      };
      viewport.addEventListener('scroll', onScroll, { passive: true });
      onCleanup(() => viewport?.removeEventListener('scroll', onScroll));
    }

    const bindNav = (sel: string, dir: -1 | 1) => {
      const btn = document.querySelector(sel) as HTMLButtonElement | null;
      if (!btn) return;
      const onClick = () => {
        if (!viewport || !strip) return;
        const badge = strip.querySelector('.history-badge') as HTMLElement | null;
        const gap = parseFloat(getComputedStyle(strip).columnGap || '0') || 0;
        const step = badge ? (badge.offsetWidth + gap) * 3.4 : viewport.clientWidth * 0.7;
        viewport.scrollBy({ left: step * dir, behavior: reducedMotion() ? 'auto' : 'smooth' });
        if (dir < 0) userScroll = true;
        else userScroll = !(viewport.scrollLeft + viewport.clientWidth >= viewport.scrollWidth - 2);
      };
      btn.addEventListener('click', onClick);
      onCleanup(() => btn.removeEventListener('click', onClick));
    };
    bindNav('.round-history-top .rh-nav.rh-left', -1);
    bindNav('.round-history-top .rh-nav.rh-right', 1);

    if (viewport) {
      ro = new ResizeObserver(() => syncOverflow());
      ro.observe(viewport);
      if (strip) ro.observe(strip);
      onCleanup(() => ro?.disconnect());
    }

    seed();

    game.on('crash', (e: any) => {
      const round: Round | undefined = e?.data?.round;
      const mult: number | undefined = e?.data?.multiplier || round?.multiplier;
      if (typeof mult === 'number') push(round?.id || `rnd_${Date.now()}`, mult, true);
      show();
    });

    game.on('historyUpdate', (e: any) => {
      const arr = e?.data?.history as Round[] | undefined;
      if (!arr?.length) return;
      setItems(arr.slice(0, MAX_HISTORY_COMPACT).reverse().map(r => ({ id: r.id, multiplier: r.multiplier, tier: badgeTier(r.multiplier) })));
      queueMicrotask(() => { syncOverflow(); if (!userScroll) scrollEnd(false); });
      show();
    });

    onCleanup(() => {});
  };

  onMount(() => {
    // ── Real mode: subscribe to server crash events ──
    if (isRealMode) {
      wrapper = document.querySelector('.round-history-top.compact');
      viewport = document.querySelector('.round-history-top .round-history-viewport');
      strip = document.querySelector('#round-history-top-strip');
      if (wrapper) wrapper.setAttribute('data-overflow', 'false');

      if (viewport) {
        const onScroll = () => {
          userScroll = !(viewport!.scrollLeft + viewport!.clientWidth >= viewport!.scrollWidth - 2);
        };
        viewport.addEventListener('scroll', onScroll, { passive: true });
        onCleanup(() => viewport?.removeEventListener('scroll', onScroll));

        ro = new ResizeObserver(() => syncOverflow());
        ro.observe(viewport);
        if (strip) ro.observe(strip);
        onCleanup(() => ro?.disconnect());
      }

      const bindNav = (sel: string, dir: -1 | 1) => {
        const btn = document.querySelector(sel) as HTMLButtonElement | null;
        if (!btn) return;
        const onClick = () => {
          if (!viewport || !strip) return;
          const badge = strip.querySelector('.history-badge') as HTMLElement | null;
          const gap = parseFloat(getComputedStyle(strip).columnGap || '0') || 0;
          const step = badge ? (badge.offsetWidth + gap) * 3.4 : viewport.clientWidth * 0.7;
          viewport.scrollBy({ left: step * dir, behavior: reducedMotion() ? 'auto' : 'smooth' });
          if (dir < 0) userScroll = true;
          else userScroll = !(viewport.scrollLeft + viewport.clientWidth >= viewport.scrollWidth - 2);
        };
        btn.addEventListener('click', onClick);
        onCleanup(() => btn.removeEventListener('click', onClick));
      };
      bindNav('.round-history-top .rh-nav.rh-left', -1);
      bindNav('.round-history-top .rh-nav.rh-right', 1);

      createEffect(() => {
        applyStoreHistory();
      });

      const unsub = gameApi.on('round:crashed', (payload) => {
        push(`rnd_${payload.roundId}`, payload.crashPoint, true);
        show();
      });

      onCleanup(() => {
        unsub();
      });
      show();
    } else {
      // ── Mock mode: subscribe to Game engine ──
      attach();
      if (!mounted) {
        const onReady = () => { attach(); };
        window.addEventListener('memePump:ready', onReady, { once: true });
        onCleanup(() => window.removeEventListener('memePump:ready', onReady));
      }
    }
    onCleanup(() => { if (timer) window.clearTimeout(timer); });
  });

  return (
    <For each={items()}>
      {(b, i) => {
        const f = formatMultiplier(b.multiplier);
        const last = () => i() === items().length - 1;
        const fresh = () => latest() === b.id;
        return (
          <div
            class={`history-badge tier-${b.tier} ${last() ? 'is-latest' : ''} ${fresh() ? 'is-new' : ''}`}
            data-round={b.id}
            data-multiplier={b.multiplier.toFixed(2)}
            data-len={f.text.length > 7 ? 'exp' : 'std'}
            title={f.title}
          >
            <span class="history-badge__text">{f.text}</span>
            {last() ? <span class="history-badge__marker" aria-hidden="true" /> : null}
          </div>
        );
      }}
    </For>
  );
}
